<template>
  <div class="credential-management">
    <div class="cm-header">
      <h3>Credential Management</h3>
      <p class="cm-subtitle">Manage access credentials for this cast</p>
    </div>

    <!-- Issue New Credential Section -->
    <div class="cm-section">
      <div class="cm-section-header">
        <h4>Issue New Credential</h4>
      </div>
      <div class="cm-form">
        <div class="cm-form-group">
          <label>Wallet Address or DID</label>
          <input
            v-model="newCredential.addressOrDid"
            type="text"
            placeholder="0x... or did:ethr:0x..."
            :disabled="issuing"
          />
        </div>
        <div class="cm-form-row">
          <div class="cm-form-group">
            <label>Access Level</label>
            <select v-model="newCredential.accessLevel" :disabled="issuing">
              <option value="viewer">Viewer</option>
              <option value="participant">Participant</option>
              <option value="presenter">Presenter</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>
        </div>
        <div class="cm-form-group">
          <label>Permissions</label>
          <div class="cm-permissions">
            <label v-for="perm in availablePermissions" :key="perm.value" class="cm-checkbox">
              <input
                type="checkbox"
                :value="perm.value"
                v-model="newCredential.permissions"
                :disabled="issuing"
              />
              {{ perm.label }}
            </label>
          </div>
        </div>
        <button
          class="cm-button primary"
          @click="issueCredential"
          :disabled="issuing || !isValidInput"
        >
          <span v-if="!issuing">Issue Credential</span>
          <span v-else>Issuing...</span>
        </button>
      </div>
    </div>

    <!-- Bulk Issue Section -->
    <div class="cm-section">
      <div class="cm-section-header">
        <h4>Bulk Issue</h4>
        <button class="cm-button-small" @click="showBulkForm = !showBulkForm">
          {{ showBulkForm ? 'Hide' : 'Show' }}
        </button>
      </div>
      <div v-if="showBulkForm" class="cm-form">
        <div class="cm-form-group">
          <label>Wallet Addresses (one per line)</label>
          <textarea
            v-model="bulkAddresses"
            placeholder="0x1234...&#10;0x5678...&#10;did:ethr:0x..."
            rows="4"
            :disabled="bulkIssuing"
          ></textarea>
        </div>
        <div class="cm-form-row">
          <div class="cm-form-group">
            <label>Access Level (for all)</label>
            <select v-model="bulkAccessLevel" :disabled="bulkIssuing">
              <option value="viewer">Viewer</option>
              <option value="participant">Participant</option>
              <option value="presenter">Presenter</option>
            </select>
          </div>
        </div>
        <button
          class="cm-button primary"
          @click="bulkIssueCredentials"
          :disabled="bulkIssuing || !bulkAddresses.trim()"
        >
          <span v-if="!bulkIssuing">Issue to All</span>
          <span v-else>Issuing {{ bulkProgress }} / {{ bulkTotal }}...</span>
        </button>
      </div>
    </div>

    <!-- Existing Credentials List -->
    <div class="cm-section">
      <div class="cm-section-header">
        <h4>Issued Credentials</h4>
        <button class="cm-button-small" @click="loadCredentials" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>

      <div v-if="loading" class="cm-loading">
        <div class="cm-spinner"></div>
        <p>Loading credentials...</p>
      </div>

      <div v-else-if="credentials.length === 0" class="cm-empty">
        <p>No credentials issued yet.</p>
      </div>

      <div v-else class="cm-credentials-list">
        <div
          v-for="cred in credentials"
          :key="cred.id"
          class="cm-credential-item"
          :class="{ revoked: cred.revoked }"
        >
          <div class="cm-credential-info">
            <div class="cm-credential-did">{{ formatDid(cred.did) }}</div>
            <div class="cm-credential-meta">
              <span class="cm-badge" :class="cred.accessLevel">{{ cred.accessLevel }}</span>
              <span v-if="cred.revoked" class="cm-badge revoked">Revoked</span>
              <span class="cm-date">{{ formatDate(cred.createdAt) }}</span>
            </div>
          </div>
          <div class="cm-credential-actions">
            <button
              v-if="!cred.revoked"
              class="cm-button-icon danger"
              @click="revokeCredential(cred)"
              :disabled="revoking === cred.id"
              title="Revoke credential"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import credentialsService from '@/services/credentialsService';

export default {
  name: 'CredentialManagement',
  props: {
    roomId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      issuing: false,
      revoking: null,
      bulkIssuing: false,
      bulkProgress: 0,
      bulkTotal: 0,
      showBulkForm: false,
      credentials: [],
      newCredential: {
        addressOrDid: '',
        accessLevel: 'participant',
        permissions: ['view', 'chat'],
      },
      bulkAddresses: '',
      bulkAccessLevel: 'participant',
      availablePermissions: [
        { value: 'view', label: 'View Cast' },
        { value: 'chat', label: 'Chat' },
        { value: 'share_screen', label: 'Share Screen' },
        { value: 'record', label: 'Record' },
        { value: 'manage_participants', label: 'Manage Participants' },
      ],
    };
  },
  computed: {
    isValidInput() {
      const input = this.newCredential.addressOrDid.trim();
      if (!input) return false;
      // Check if it's a valid Ethereum address or DID
      const isAddress = /^0x[a-fA-F0-9]{40}$/.test(input);
      const isDid = input.startsWith('did:ethr:');
      return isAddress || isDid;
    },
  },
  mounted() {
    this.loadCredentials();
  },
  methods: {
    async loadCredentials() {
      this.loading = true;
      try {
        const result = await credentialsService.getCredentialsByRoom(this.roomId);
        this.credentials = result.credentials || result || [];
      } catch (error) {
        console.error('Failed to load credentials:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load credentials',
          color: 'danger',
        });
      } finally {
        this.loading = false;
      }
    },

    getDid(addressOrDid) {
      const input = addressOrDid.trim();
      if (input.startsWith('did:ethr:')) {
        return input;
      }
      return credentialsService.getDIDFromAddress(input);
    },

    async issueCredential() {
      this.issuing = true;
      try {
        const did = this.getDid(this.newCredential.addressOrDid);
        await credentialsService.issueCredential(
          did,
          this.roomId,
          this.newCredential.accessLevel,
          this.newCredential.permissions
        );

        this.$vs.notify({
          title: 'Success',
          text: 'Credential issued successfully',
          color: 'success',
        });

        // Reset form
        this.newCredential.addressOrDid = '';
        this.newCredential.accessLevel = 'participant';
        this.newCredential.permissions = ['view', 'chat'];

        // Reload list
        await this.loadCredentials();
      } catch (error) {
        console.error('Failed to issue credential:', error);
        this.$vs.notify({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to issue credential',
          color: 'danger',
        });
      } finally {
        this.issuing = false;
      }
    },

    async bulkIssueCredentials() {
      const addresses = this.bulkAddresses
        .split('\n')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      if (addresses.length === 0) return;

      this.bulkIssuing = true;
      this.bulkProgress = 0;
      this.bulkTotal = addresses.length;

      const results = { success: 0, failed: 0 };

      for (const address of addresses) {
        try {
          const did = this.getDid(address);
          await credentialsService.issueCredential(
            did,
            this.roomId,
            this.bulkAccessLevel,
            ['view', 'chat']
          );
          results.success++;
        } catch (error) {
          console.error(`Failed to issue to ${address}:`, error);
          results.failed++;
        }
        this.bulkProgress++;
      }

      this.$vs.notify({
        title: 'Bulk Issue Complete',
        text: `Success: ${results.success}, Failed: ${results.failed}`,
        color: results.failed > 0 ? 'warning' : 'success',
      });

      this.bulkAddresses = '';
      this.bulkIssuing = false;
      await this.loadCredentials();
    },

    async revokeCredential(credential) {
      if (!confirm(`Are you sure you want to revoke access for ${this.formatDid(credential.did)}?`)) {
        return;
      }

      this.revoking = credential.id;
      try {
        await credentialsService.revokeCredential(credential.did, this.roomId, 'Revoked by cast owner');

        this.$vs.notify({
          title: 'Success',
          text: 'Credential revoked successfully',
          color: 'success',
        });

        await this.loadCredentials();
      } catch (error) {
        console.error('Failed to revoke credential:', error);
        this.$vs.notify({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to revoke credential',
          color: 'danger',
        });
      } finally {
        this.revoking = null;
      }
    },

    formatDid(did) {
      if (!did) return '';
      if (did.length <= 24) return did;
      return `${did.slice(0, 16)}...${did.slice(-8)}`;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.credential-management {
  padding: 20px;
  background: #1f272f;
  border-radius: 8px;
  color: #a6a6a8;
}

.cm-header {
  margin-bottom: 24px;
}

.cm-header h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.cm-subtitle {
  margin: 0;
  font-size: 13px;
  color: #647181;
}

.cm-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #181e24;
  border-radius: 8px;
  border: 1px solid #31394e;
}

.cm-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.cm-section-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #d7df23;
}

.cm-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cm-form-row {
  display: flex;
  gap: 16px;
}

.cm-form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cm-form-group label {
  font-size: 12px;
  font-weight: 500;
  color: #647181;
}

.cm-form-group input,
.cm-form-group select,
.cm-form-group textarea {
  padding: 10px 12px;
  background: #1d232b;
  border: 1px solid #31394e;
  border-radius: 6px;
  color: #a6a6a8;
  font-size: 13px;
}

.cm-form-group input:focus,
.cm-form-group select:focus,
.cm-form-group textarea:focus {
  outline: none;
  border-color: #d7df23;
}

.cm-form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.cm-permissions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cm-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}

.cm-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.cm-button {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.cm-button.primary {
  background: #d7df23;
  color: #181a20;
}

.cm-button.primary:hover:not(:disabled) {
  background: #c5cd1f;
}

.cm-button.primary:disabled {
  background: #4a4a4a;
  color: #888;
  cursor: not-allowed;
}

.cm-button-small {
  padding: 6px 12px;
  background: #31394e;
  color: #a6a6a8;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}

.cm-button-small:hover:not(:disabled) {
  background: #3d4660;
}

.cm-button-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #31394e;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.cm-button-icon.danger {
  color: #ef4444;
}

.cm-button-icon.danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

.cm-button-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cm-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
}

.cm-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #31394e;
  border-top-color: #d7df23;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.cm-empty {
  text-align: center;
  padding: 24px;
  color: #647181;
}

.cm-credentials-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cm-credential-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #1d232b;
  border-radius: 6px;
  border: 1px solid #31394e;
}

.cm-credential-item.revoked {
  opacity: 0.6;
}

.cm-credential-info {
  flex: 1;
}

.cm-credential-did {
  font-family: monospace;
  font-size: 13px;
  color: #fff;
  margin-bottom: 6px;
}

.cm-credential-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cm-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.cm-badge.viewer {
  background: #374151;
  color: #9ca3af;
}

.cm-badge.participant {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.cm-badge.presenter {
  background: rgba(168, 85, 247, 0.2);
  color: #c084fc;
}

.cm-badge.moderator {
  background: rgba(215, 223, 35, 0.2);
  color: #d7df23;
}

.cm-badge.revoked {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.cm-date {
  font-size: 11px;
  color: #647181;
}

.cm-credential-actions {
  display: flex;
  gap: 8px;
}
</style>
